import React, { useMemo, useState } from "react";
import AdminLayout from "../admin/AdminDashboardLayout";
import Button from "../../utils/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useApiMutation } from "../../hooks/useMutation";
import { useApiQuery } from "../../hooks/useApiQuery";
import { ApiRequestOptions } from "../../utils/api";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "@mui/material/Tooltip";
import { useDashboardRole } from "../../hooks/useDashboardRole";

type ExportType = "VOTER_REGISTRATIONS" | "REGISTRATION_CHECKS" | "FIELD_ACTIVITY";

type ExportStatus = "PENDING" | "APPROVED" | "REJECTED";

type ExportRequestRow = {
  id: number;
  exportType: ExportType | string;
  status: ExportStatus | string;
  requestedBy: string;
  requestedAt: string;
  approvedAt: string | null;
  downloadUrl?: string;
  fileUrl?: string;
};

type ExportRequestsResponse = {
  success: boolean;
  message: string;
  data: {
    content: ExportRequestRow[];
    empty: boolean;
    first: boolean;
    last: boolean;
    number: number;
    numberOfElements: number;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
      paged: boolean;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      unpaged: boolean;
    };
    size: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    totalElements: number;
    totalPages: number;
  };
};

function getStatusClasses(status: string) {
  const normalized = status?.toUpperCase();
  if (normalized === "APPROVED" || normalized === "COMPLETED") return "bg-green-100 text-green-800";
  if (normalized === "REJECTED") return "bg-red-100 text-red-800";
  return "bg-yellow-100 text-yellow-800";
}

function formatDateString(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
  } catch {
    return dateString;
  }
}

export default function PartnerExports() {
  const { dashboardRole } = useDashboardRole();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>("VOTER_REGISTRATIONS");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [exportFormat, setExportFormat] = useState<"csv" | "pdf">("csv");
  const [reason, setReason] = useState("Audit purposes");

  const [selectedRequest, setSelectedRequest] = useState<ExportRequestRow | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const limit = 10;
  const [page, setPage] = useState(1);

  const exportRequestsQuery = useApiQuery<ExportRequestsResponse>({
    queryKey: ["data-exports-find", page, limit],
    request: {
      method: "GET",
      url: "/data-exports/find",
      // params: { page, limit },
    } as ApiRequestOptions,
  });

  const apiRows = exportRequestsQuery.data?.data?.content || [];

  const totalPages = useMemo(() => {
    const total = exportRequestsQuery.data?.data?.totalPages;
    if (typeof total === "number" && total >= 0) {
      return Math.max(1, total);
    }
    if (apiRows.length < limit) return 1;
    return page + 1;
  }, [exportRequestsQuery.data?.data?.totalPages, limit, page, apiRows.length]);

  const requestExportMutation = useApiMutation({
    onMutate: () => {
      toast.loading("Requesting export...", { id: "export-request-toast" });
    },
    onSuccess: () => {
      toast.success(
        "please wait!, your export request is awating approval from the admin",
        { id: "export-request-toast" }
      );
      queryClient.invalidateQueries({ queryKey: ["data-exports-find"] });
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Export request failed", {
        id: "export-request-toast",
      });
    },
  });

  const canRequestExport = Boolean(startDate && endDate && exportType && reason);

  const openDetails = (row: ExportRequestRow) => {
    setSelectedRequest(row);
    setIsDetailsModalOpen(true);
  };

  const closeDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleCopyDownloadUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Download URL copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleRequestExport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select a start date and end date");
      return;
    }

    const start = format(startDate, "yyyy-MM-dd");
    const end = format(endDate, "yyyy-MM-dd");

    await requestExportMutation.mutateAsync({
      method: "POST",
      url: `/data-exports`,
      // url: `/data-exports?startDate=${start}&endDate=${end}`,
      data: {
        reason,
        exportType,
        fromDate: start,
        endDate: end,
      },
    });
  };

  return (
    <AdminLayout role={dashboardRole}>
      <div className="bg-gray-50 rounded-xl min-h-screen p-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Exports</h2>
            <p className="text-sm text-gray-500">Request and track data exports</p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="medium"
              label="Export Data"
              additionalClassname="!w-auto px-4"
              onClick={() => setIsModalOpen(true)}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Export Requests
            </h3>
            <p className="text-sm text-gray-500">
              Showing your most recent export requests
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request ID
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Export Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exportRequestsQuery.isLoading ? (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={5}>
                      Loading...
                    </td>
                  </tr>
                ) : apiRows.length === 0 ? (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={5}>
                      No export requests found.
                    </td>
                  </tr>
                ) : (
                  apiRows.map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetails(r)}
                    >
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{r.id}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {r.exportType.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {r.requestedBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDateString(r.requestedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusClasses(
                            r.status
                          )}`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  page <= 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                }`}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  page >= totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                }`}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-black/50"
              onMouseDown={() => setIsModalOpen(false)}
            />

            <div
              className="relative z-10 w-[720px] max-w-[95%] bg-white rounded-xl shadow-2xl p-6 text-gray-900 border border-gray-200"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Request Export</h2>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-900"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Export Type
                  </label>
                  <select
                    value={exportType}
                    onChange={(e) => setExportType(e.target.value as ExportType)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="VOTER_REGISTRATIONS">Voter Registrations</option>
                    <option value="AUDIT_LOGS">Audit Logs</option>
                    <option value="ACTIVITY_LOGS">Activity Logs</option>
                    <option value="MESSAGING_USAGE">Messaging Usage & Billing Summary</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Export As
                  </label>
                  <div className="flex items-center gap-4 h-[42px]">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="csv"
                        checked={exportFormat === "csv"}
                        onChange={() => setExportFormat("csv")}
                      />
                      CSV
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="pdf"
                        checked={exportFormat === "pdf"}
                        onChange={() => setExportFormat("pdf")}
                      />
                      PDF
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={(d: Date | null) => setStartDate(d)}
                    dateFormat="yyyy-MM-dd"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    maxDate={endDate || undefined}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <DatePicker
                    selected={endDate}
                    onChange={(d: Date | null) => setEndDate(d)}
                    dateFormat="yyyy-MM-dd"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    minDate={startDate || undefined}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <Button
                  variant="primary"
                  size="medium"
                  label={requestExportMutation.isPending ? "Requesting..." : "Request Export"}
                  additionalClassname="!w-auto px-4"
                  disabled={!canRequestExport || requestExportMutation.isPending}
                  onClick={handleRequestExport}
                />
              </div>
            </div>
          </div>
        )}

        {isDetailsModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div className="absolute inset-0 bg-black/50" onMouseDown={closeDetails} />

            <div
              className="relative z-10 w-[720px] max-w-[95%] bg-white rounded-xl shadow-2xl p-6 text-gray-900 border border-gray-200"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Export Request Details</h2>
                <button type="button" onClick={closeDetails} className="text-gray-500 hover:text-gray-900">
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Request ID</div>
                  <div className="text-sm font-medium text-gray-900">#{selectedRequest.id}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Export Type</div>
                  <div className="text-sm font-medium text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedRequest.exportType.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Requested By</div>
                  <div className="text-sm font-medium text-gray-900">{selectedRequest.requestedBy}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date Requested</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateString(selectedRequest.requestedAt)}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</div>
                  <div className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusClasses(
                        selectedRequest.status
                      )}`}
                    >
                      {selectedRequest.status}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Approved At</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateString(selectedRequest.approvedAt)}</div>
                </div>
              </div>

              {selectedRequest.status?.toUpperCase() === "COMPLETED" && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Download URL</div>
                  {selectedRequest.fileUrl ? (
                    <Tooltip title="Click to copy" placement="top" arrow>
                      <button
                        type="button"
                        onClick={() => handleCopyDownloadUrl(selectedRequest.fileUrl as string)}
                        className="w-full text-left px-4 py-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 font-mono text-sm hover:bg-blue-100 transition-colors"
                      >
                        {selectedRequest.fileUrl}
                      </button>
                    </Tooltip>
                  ) : (
                    <div className="px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 text-sm">
                      No download url available
                    </div>
                  )}
                </div>
              )}

              {selectedRequest.status?.toUpperCase() === "REJECTED" && (
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-400 text-black hover:bg-amber-500 transition-colors"
                    onClick={() => toast.success("Appeal submitted")}
                  >
                    Appeal
                  </button>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  onClick={closeDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
